import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { getRouteApi, Link } from '@tanstack/react-router';
import { Button, LoadingBlock, Notice, Section, Tag } from '@wodi/ui';

import { userQuery } from '../api/account-security.ts';
import { ApiError } from '../api/client.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { UserSecuritySection } from '../settings/user-security.tsx';
import { SettingsPage } from './settings-nav.tsx';

// Settings > Users > a person (ADR 0037): where they work and their sign-in security.

const route = getRouteApi('/_app/settings/users/$userId');

const back = <Link to="/settings/users">Users</Link>;

export function UserPage() {
  const { userId } = route.useParams();
  const online = useOnline();
  const { data: session } = useSuspenseQuery(sessionQuery);
  const allowed = session !== null && can(session, 'admin.users.read');
  const user = useQuery({ ...userQuery(userId), enabled: allowed });

  if (session === null || !allowed) {
    return (
      <SettingsPage title="User" back={back} intro={false}>
        <Notice tone="info">
          You don&apos;t have permission to see users. Ask an administrator if you need it.
        </Notice>
      </SettingsPage>
    );
  }
  if (user.isPending) {
    return (
      <SettingsPage title="User" back={back} intro={false}>
        <LoadingBlock label="Loading the user" rows={4} />
      </SettingsPage>
    );
  }
  if (user.isError) {
    return (
      <SettingsPage title="User" back={back} intro={false}>
        <Notice
          tone="error"
          action={
            <Button dense isDisabled={!online} onPress={() => void user.refetch()}>
              Try again
            </Button>
          }
        >
          {!online
            ? "You're offline. The user can be loaded once you reconnect."
            : user.error instanceof ApiError
              ? user.error.message
              : "The user couldn't be loaded. Try again."}
        </Notice>
      </SettingsPage>
    );
  }
  const person = user.data;
  return (
    <SettingsPage title={person.displayName} back={back} intro={person.email}>
      <Section title="Roles" description="What they can do, at each facility.">
        <p>
          <Tag state={person.status === 'active' ? 'active' : 'neutral'}>
            {person.status === 'active' ? 'Active' : 'Disabled'}
          </Tag>
        </p>
        {person.facilityRoles.length === 0 ? (
          <p>No roles yet.</p>
        ) : (
          <ul>
            {person.facilityRoles.map((r) => (
              <li key={`${r.facilityId}:${r.roleId}`}>
                {r.facilityName}: {r.roleName}
              </li>
            ))}
          </ul>
        )}
      </Section>
      <UserSecuritySection userId={person.id} name={person.displayName} session={session} />
    </SettingsPage>
  );
}
